/* eslint-disable react/no-array-index-key */
import CheckIcon from '@mui/icons-material/Check'
import {
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { type MRT_SortingFn } from 'material-react-table'
import { ReactNode } from 'react'
import { formModes } from '../../utils/constants'
import {
  FarmSubdivision,
  FieldDefinition,
  FormDefinition,
  MetricEvent,
} from '../../types/apiModels'
import { findRefDataById } from '../../utils/dataCollection/optionsGetters/findRefDataById'
import { formatNumber } from '../../utils/formatNumber'
import { BooleanRowData } from './BooleanRowData'
import { FindingsRowData } from './FindingsRowData'
import { MetricEventStatusRowData } from './MetricEventStatusRowData'

export type FormMode = (typeof formModes)[keyof typeof formModes]

const PADDOCKS_KIND_RANK = { general: 0, named: 1, deleted: 2 } as const

type PaddocksKind = keyof typeof PADDOCKS_KIND_RANK

export type MetricEventRow = Partial<
  Omit<MetricEvent, 'Findings' | 'isVerified' | 'metricStatusId'> & {
    findings: ReactNode
    isVerified: ReactNode
    metricStatusId: ReactNode
    paddocksKind: PaddocksKind
  }
>

export type ColumnOption = {
  name: string
  label: string
  options: {
    display?: boolean
    filter?: boolean
    sort?: boolean
    width?: number
    customBodyRender?: (_value: unknown) => ReactNode
    sortingFn?: MRT_SortingFn<MetricEventRow>
  }
}

const dataFieldTypes = [
  'TextField',
  'DateField',
  'Select',
  'RadioGroup',
  'CheckBox',
  'CheckBoxGroup',
  'FieldArray',
  'FileInput',
]

type CellValue =
  | {
      value: string | number
      unit?: string
    }
  | string
  | number
  | boolean

const isNumericCell = (value: unknown): boolean => {
  if (typeof value === 'number') return Number.isFinite(value)
  return typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))
}

const processFieldArray = async (
  fieldArrayData: unknown[],
  fieldDefinition: FieldDefinition,
): Promise<ReactNode> => {
  const columnHeaders = fieldDefinition?.fields?.map(field => field.label) || []

  const processedData = await Promise.all(
    fieldArrayData.map(async nestedRecord => {
      const rowData = await Promise.all(
        fieldDefinition?.fields?.map(async (field: FieldDefinition) => {
          const value = nestedRecord[field.name]

          if (field.refDataKey) {
            const refValue = await findRefDataById(field.refDataKey, value as string | number)
            return refValue?.es_AR || ''
          }

          if (typeof value === 'boolean') {
            return value ? 'Sí' : 'No'
          }
          if (value === 'true' || value === 'false') {
            return value === 'true' ? 'Sí' : 'No'
          }

          if (value && field.unit) {
            return { value: formatNumber(value), unit: field.unit }
          }

          return formatNumber(value) || ''
        }) || [],
      )

      return rowData
    }),
  )
  return (
    <TableContainer component={Paper} sx={{ marginTop: '8px' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columnHeaders.map((header: string) => (
              <TableCell key={header}>
                <Typography fontWeight="bold" variant="caption">
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {processedData.map((row: CellValue[], rowIndex: number) => (
            <TableRow key={rowIndex}>
              {row.map((cell: CellValue, cellIndex: number) => {
                const cellValue = typeof cell === 'object' && 'value' in cell ? cell.value : cell
                const cellUnit = typeof cell === 'object' && 'unit' in cell ? cell.unit : ''
                return (
                  <TableCell key={cellIndex} align={isNumericCell(cellValue) ? 'right' : 'left'}>
                    <Typography component="span" variant="body2">
                      {cellValue}
                      {cellUnit && ` ${cellUnit}`}
                    </Typography>
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// Stored file names are not guaranteed to be valid percent-encodings, and decodeURIComponent throws on them
const safeDecodeURIComponent = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

// Persisted urls are untrusted input, so only absolute http(s) ones may reach an href
const toSafeHref = (url: unknown): string | undefined => {
  if (typeof url !== 'string') return undefined
  try {
    const { protocol } = new URL(url)
    return protocol === 'http:' || protocol === 'https:' ? url : undefined
  } catch {
    return undefined
  }
}

const processFileInput = (fileInputData: string[]): ReactNode => {
  return (
    <Stack gap="8px">
      {fileInputData.map((fileUrl: string) => {
        const fileName = safeDecodeURIComponent(fileUrl.split('/').pop())
        const href = toSafeHref(fileUrl)
        return href ? (
          <Link
            key={fileUrl}
            href={href}
            sx={{ textOverflow: 'ellipsis', display: 'block', overflow: 'hidden' }}
            target="_blank"
          >
            {fileName}
          </Link>
        ) : (
          <Typography key={fileUrl} component="span" variant="body2">
            {fileName}
          </Typography>
        )
      })}
    </Stack>
  )
}

const formatDate = (date: string): string => {
  return date ? date.split('-').reverse().join('/') : ''
}

const processRefDataField = async (
  value: unknown,
  fieldDefinition: FieldDefinition | undefined,
): Promise<string | ReactNode> => {
  if (fieldDefinition?.refDataKey) {
    if (Array.isArray(value)) {
      const values = await Promise.all(
        value.map((id: string | number) =>
          findRefDataById(fieldDefinition.refDataKey as string, id),
        ),
      )
      return values.map(v => v?.es_AR || '').join(' - ')
    }
    const refValue = await findRefDataById(fieldDefinition.refDataKey, value as string | number)
    return refValue?.es_AR || ''
  }
  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No'
  }
  if (value === 'true' || value === 'false') {
    return value === 'true' ? 'Sí' : 'No'
  }

  if (value && fieldDefinition?.unit) {
    return `${formatNumber(value)} ${fieldDefinition.unit}`
  }

  return formatNumber(value) || ''
}

type ProcessRecordDataParams = {
  record: MetricEvent
  formDefinition: FormDefinition
  farmSubdivision?: FarmSubdivision
  formMode?: FormMode
  handleMetricReset?: () => void
}

const paddockCollator = new Intl.Collator('es', { numeric: true, sensitivity: 'base' })

const comparePaddocksRows = (a: MetricEventRow, b: MetricEventRow): number =>
  PADDOCKS_KIND_RANK[a.paddocksKind] - PADDOCKS_KIND_RANK[b.paddocksKind] ||
  paddockCollator.compare(String(a.paddocks ?? ''), String(b.paddocks ?? ''))

export const sortRowsByPaddocks = (rows: MetricEventRow[]): MetricEventRow[] =>
  [...rows].sort(comparePaddocksRows)

const buildPaddocksFields = (
  selectedPaddocks: string[] | undefined,
  farmSubdivision: FarmSubdivision | undefined,
): Pick<MetricEventRow, 'paddocks' | 'paddocksKind'> => {
  if (!selectedPaddocks?.length) return { paddocks: 'General', paddocksKind: 'general' }

  const names = selectedPaddocks
    .map(id => farmSubdivision?.features?.find(f => f.properties.id === id)?.properties?.name)
    .filter(Boolean)
    .sort(paddockCollator.compare)

  return names.length
    ? { paddocks: names.join(' - '), paddocksKind: 'named' }
    : { paddocks: 'Lote eliminado', paddocksKind: 'deleted' }
}

export const processRecordData = async ({
  record,
  formDefinition,
  farmSubdivision,
  formMode,
  handleMetricReset,
}: ProcessRecordDataParams): Promise<MetricEventRow> => {
  const data = record.isDraft ? record : record.data

  const row: MetricEventRow = {
    id: record.id,
    isDraft: record.isDraft,
    metricYear: data.metricYear,
    notApplicable: data.notApplicable,
    ...buildPaddocksFields(data.selectedPaddocks, farmSubdivision),
    findings: (
      <FindingsRowData
        findings={record?.Findings || []}
        handleMetricReset={handleMetricReset}
        readOnly={formMode !== formModes.Review}
      />
    ),
    isVerified: <BooleanRowData value={record?.isVerified} />,
    metricStatusId: (
      <MetricEventStatusRowData
        handleMetricReset={handleMetricReset}
        metricId={record?.id || ''}
        statusId={record?.metricStatusId ?? null}
      />
    ),
  }

  const componentProcessors: Record<
    string,
    (_value: unknown, _definition?: FieldDefinition) => Promise<ReactNode> | ReactNode
  > = {
    FieldArray: (value, definition) =>
      processFieldArray(value as unknown[], definition as FieldDefinition),
    FileInput: value => processFileInput(value as string[]),
    DateField: value => formatDate(value as string),
    default: (value, definition) => processRefDataField(value, definition),
  }

  const fieldDefinitionMap = formDefinition.fields.reduce<Record<string, FieldDefinition>>(
    (acc, field) => {
      return { ...acc, [field.name]: field }
    },
    {},
  )

  const fieldsToProcess = Object.keys(data).filter(
    key =>
      ![
        'id',
        'paddocks',
        'findings',
        'isVerified',
        'metricStatusId',
        'isDraft',
        'metricYear',
        'notApplicable',
      ].includes(key) && fieldDefinitionMap[key],
  )

  await Promise.all(
    fieldsToProcess.map(async key => {
      const fieldDefinition = fieldDefinitionMap[key]
      const processor =
        componentProcessors[fieldDefinition?.component_type] || componentProcessors.default
      const value = await processor(data[key], fieldDefinition)
      if (value !== null) {
        row[key] = value
      }
    }),
  )

  return row
}

type SpecificColumns = (keyof MetricEvent)[]

export const createColumns = async (
  formDefinition: FormDefinition,
  specificColumns: SpecificColumns,
  columnOptions: Partial<ColumnOption>[] = [],
): Promise<ColumnOption[]> => {
  const baseColumns: Record<string, ColumnOption> = {
    id: { name: 'id', label: 'id', options: { display: false } },
    isDraft: {
      name: 'isDraft',
      label: 'Borrador',
      options: {
        customBodyRender: (value): ReactNode => {
          if (value) {
            return <Chip color="warning" label="Borrador" />
          }
          return null
        },
        filter: true,
        sort: true,
      },
    },
    metricYear: { name: 'metricYear', label: 'Año', options: {} },
    paddocks: {
      name: 'paddocks',
      label: 'Lote/s',
      options: {
        // TanStack's built-in sorts see only the label, so they can't rank General and deleted rows by paddock kind.
        sortingFn: (rowA, rowB): number => comparePaddocksRows(rowA.original, rowB.original),
      },
    },
    isVerified: {
      name: 'isVerified',
      label: 'Verificada',
      options: {
        filter: true,
        sort: true,
      },
    },
    findings: {
      name: 'findings',
      label: 'Observaciones',
      options: {
        filter: false,
        sort: true,
      },
    },
    notApplicable: {
      name: 'notApplicable',
      label: 'No aplica',
      options: {
        customBodyRender: (value: unknown): ReactNode =>
          (value as boolean) ? <CheckIcon color="success" /> : null,
      },
    },
    metricStatusId: {
      name: 'metricStatusId',
      label: 'Estado',
      options: { filter: false, sort: true },
    },
  }

  const fieldDefinitionMap = formDefinition.fields.reduce<Record<string, FieldDefinition>>(
    (acc, field) => {
      if (field.label && field.name && dataFieldTypes.includes(field.component_type)) {
        return { ...acc, [field.name]: field }
      }
      return acc
    },
    {},
  )

  const formFieldColumns = Object.keys(fieldDefinitionMap).map(fieldName => {
    const field = fieldDefinitionMap[fieldName]
    return {
      name: field.name,
      label: field.label,
      options: {
        customBodyRender: (value: unknown): ReactNode => {
          if (field.component_type === 'DateField') return formatDate(value as string)
          // MUI RadioGroup emits its selected value as a string, so its boolean answers arrive as 'true'/'false'
          const isRadioGroup = field.component_type === 'RadioGroup'
          const isAffirmative = value === true || (isRadioGroup && value === 'true')
          const isNegative = value === false || (isRadioGroup && value === 'false')
          if (isAffirmative || isNegative)
            return isAffirmative ? <CheckIcon color="success" /> : null
          if (Array.isArray(value)) return value.join(' - ')
          return value as ReactNode
        },
      },
    }
  })

  const allColumns = [...Object.values(baseColumns), ...formFieldColumns]

  const filteredColumns = allColumns.filter(col =>
    specificColumns.includes(col.name as keyof MetricEvent),
  )

  return filteredColumns.map(col => {
    const columnOption = columnOptions.find(co => co.name === col.name)
    if (columnOption) {
      return {
        ...col,
        ...columnOption,
        options: {
          ...col.options,
          ...columnOption.options,
        },
      }
    }
    return col
  })
}
